import { api, ApiError } from '@smena/ui';

export type ChannelTransport = 'telegram' | 'max' | 'vk' | 'web' | 'sim';
export type ChannelMode = 'personal' | 'shared_bot' | 'custom_bot' | 'link' | 'community' | 'stand';
/** `unknown` - не то же самое, что `down`: у личных каналов health-эндпоинта пока нет. */
export type ChannelHealth = 'ok' | 'down' | 'unknown';

export interface ClientChannel {
  sid: string;
  channelRef: string;
  transport: ChannelTransport;
  mode: ChannelMode;
  /** «Telegram» */
  title: string;
  /** «ваш номер» */
  sub: string;
  /** «Telegram · ваш номер» */
  label: string;
  /** `+7 921 ••• 45-67`, `@smena_guest_bot`. PII: только в карточке и в выборе канала. */
  identity: string | null;
  status: ChannelHealth;
  reason: string | null;
  messageCount: number;
  humanTookOver: boolean;
  lastAt?: string;
  /** Последнее сообщение ОТ ГОСТЯ в этом канале. */
  lastInboundAt: string | null;
  /** Гость сюда ни разу не писал (ссылка из приглашения лежит неоткрытой). */
  dormant: boolean;
}

export type ReplyMode = 'auto' | 'pinned' | 'fallback' | 'blocked';

/** Куда уйдёт ответ. Считает сервер - тем же резолвером, что и операторские боты. */
export interface ReplyChannelState {
  auto: string | null;
  pinned: string | null;
  pinnedBy: string | null;
  /** Ровно то, что уйдёт: это и показывает пилюля над полем. */
  effective: string | null;
  mode: ReplyMode;
  canSend: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system' | 'staff';
  content: string;
  ts?: string;
  channelRef?: string;
}

export interface ClientSummary {
  clientKey: string;
  guestName?: string;
  bookingId?: string;
  channels: ClientChannel[];
  totalMessages: number;
  hasEscalation: boolean;
  humanTookOver: boolean;
  lastActivityAt: string;
  lastMessage: (ChatMessage & { channelRef: string }) | null;
  lastInbound: { channelRef: string; ts: string } | null;
}

export interface EscalationEntry {
  id: string;
  sid: string;
  channelRef: string;
  ts: string;
  reason: string;
  text: string;
}

/** Бронь из KB - то, что показываем оператору в карточке справа (packages/core Booking). */
export interface BookingView {
  id?: string;
  guest?: string;
  nights?: number;
  amount?: number;
  prepaid?: boolean;
  checkin?: string;
  checkout?: string;
}

export interface ClientDetail {
  client: {
    clientKey: string;
    guestName?: string;
    bookingId?: string;
    channels: ClientChannel[];
    humanTookOver: boolean;
  };
  messages: (ChatMessage & { channelRef: string })[];
  escalations: EscalationEntry[];
  booking: BookingView | null;
  property: { title: string; city: string | null };
  replyChannel: ReplyChannelState;
}

export async function fetchClients(): Promise<ClientSummary[]> {
  const r = await api<{ clients: ClientSummary[] }>('/api/staff/clients');
  return r.clients;
}

export async function fetchClient(key: string): Promise<ClientDetail> {
  return api<ClientDetail>(`/api/staff/clients/${encodeURIComponent(key)}`);
}

/** Коды отказа сервера. Текст к каждому приходит с сервера - панель показывает его как есть. */
export type ReplyErrorCode =
  | 'channel_required'
  | 'channel_unknown'
  | 'reply_target_changed'
  | 'channel_down'
  | 'delivery_failed';

export interface ReplyFailure {
  code: ReplyErrorCode | null;
  message: string;
  replyChannel: ReplyChannelState | null;
}

/**
 * Ответ гостю. `channelRef` - ровно тот канал, который оператор видит в пилюле;
 * `expectedAutoRef` - канал последнего входящего на момент отрисовки. Сервер сверяет его
 * и отвечает 409, если гость успел написать из другого мессенджера, пока шёл набор текста.
 */
export async function replyClient(
  key: string,
  text: string,
  channelRef: string,
  expectedAutoRef: string | null,
): Promise<{ delivered: string; channelRef: string; sid: string }> {
  return api(`/api/staff/clients/${encodeURIComponent(key)}/reply`, {
    method: 'POST',
    json: { text, channelRef, expectedAutoRef },
  });
}

/**
 * «Вопрос решён»: закрывает все открытые эскалации гостя. Ответ гостю делает то же самое
 * сам, но иногда вопрос закрывается вне панели - звонком или тем, что он отпал.
 */
export async function closeClientEscalations(key: string): Promise<{ closed: number }> {
  return api(`/api/staff/clients/${encodeURIComponent(key)}/escalations/close`, {
    method: 'POST',
    json: {},
  });
}

/** `channelRef: null` - вернуть авто. */
export async function setReplyChannel(
  key: string,
  channelRef: string | null,
): Promise<{ replyChannel: ReplyChannelState }> {
  return api(`/api/staff/clients/${encodeURIComponent(key)}/reply-channel`, {
    method: 'POST',
    json: { channelRef },
  });
}

/** Разбор отказа: сервер кладёт `{code, message, replyChannel}` в тело ошибки. */
export function toReplyFailure(err: unknown): ReplyFailure {
  if (err instanceof ApiError) {
    const body = err.body as { code?: string; message?: string; replyChannel?: ReplyChannelState } | undefined;
    return {
      code: (body?.code as ReplyErrorCode | undefined) ?? null,
      message: body?.message ?? err.message,
      replyChannel: body?.replyChannel ?? null,
    };
  }
  return { code: null, message: err instanceof Error ? err.message : 'Не удалось отправить', replyChannel: null };
}

export async function takeover(sid: string): Promise<void> {
  await api(`/api/staff/sessions/${encodeURIComponent(sid)}/takeover`, {
    method: 'POST',
    json: {},
  });
}

export async function release(sid: string): Promise<void> {
  await api(`/api/staff/sessions/${encodeURIComponent(sid)}/release`, {
    method: 'POST',
    json: {},
  });
}
